/* The map canvas: one object at three depths.
 *
 * Estate  - subnets as boxes, the routed topology between them, hosts folded
 *           into each box as a dot cluster so population is still visible.
 * Segment - one subnet expands to fill the board, the rest dock along the top
 *           rim as capsules and keep their edges attached, so drilling in never
 *           costs you the context you came from.
 * Host    - clicking a host hands off to the existing AssetDrawer.
 *
 * Rendering is imperative rather than declarative. Every frame moves 50 to 500
 * nodes plus their edges and particles, which React cannot re-render at 60fps.
 * React owns the chrome and the data; this module owns one <svg> and mutates it
 * from a single requestAnimationFrame loop. The same pattern any charting
 * library uses when it mounts into a React tree.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const NS = 'http://www.w3.org/2000/svg';
const el = (n, at) => {
  const e = document.createElementNS(NS, n);
  // Skip undefined/null: setAttribute stringifies them, and `font-family
  //="undefined"` is an invalid stack that silently falls back to the browser
  // default serif rather than inheriting the page font.
  for (const k in at) if (at[k] !== undefined && at[k] !== null) e.setAttribute(k, at[k]);
  return e;
};
const UI_FONT = 'Montserrat, system-ui, sans-serif';
const MONO_FONT = 'JetBrains Mono, monospace';

const COLOR = {
  ok: '#00ce3e', healthy: '#00ce3e', none: '#00ce3e',
  approaching: '#f59e0b', medium: '#f59e0b', low: '#64748b', info: '#64748b',
  eol: '#ef4444', high: '#ef4444', critical: '#ef4444',
  unknown: '#94a3b8', pivot: '#7c4ad0',
};

const hot = (a) => a.risk === 'critical' || a.risk === 'high';
const isEol = (a) => a.eol_status === 'eol' || a.eol_status === 'approaching';
const octet = (ip) => {
  const p = String(ip || '').split('.');
  return p.length === 4 ? `.${p[3]}` : String(ip || '');
};
const shortHost = (h, max) => {
  if (!h) return '';
  const s = h.split('.')[0];
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};
const lerp = (a, b, t) => a + (b - a) * t;
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
// Honour the OS setting: with reduced motion the map jumps straight to its
// target layout instead of morphing. Also makes the layout deterministic for
// headless capture, where requestAnimationFrame does not run to completion.
const MORPH_MS = 720;
const morphMs = () => (reduceMotion() ? 1 : MORPH_MS);
const reduceMotion = () => typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function dotColor(node, mode, pivotsOn) {
  if (pivotsOn && node.multi_homed) return COLOR.pivot;
  if (mode === 'eol') return COLOR[node.eol_status] || COLOR.unknown;
  if (mode === 'criticality') {
    const CRIT = { critical: '#ef4444', high: '#ef4444', medium: '#f59e0b', low: '#00ce3e' };
    return CRIT[node.criticality] || COLOR.unknown;
  }
  return COLOR[node.risk] || COLOR.none;
}

/* ---------------- layout ---------------- */

// Gateways and routers occupy a band above the subnets; the subnets tile the
// rest. Ordering is by CIDR so a host does not move between refreshes.
function estateLayout(subnets, nodes, W, H, edges) {
  const band = nodes.length ? 118 : 54;
  const pad = 18;
  const n = Math.max(1, subnets.length);
  const availW = W - pad * 2;
  const availH = H - band - pad;
  const cols = clamp(Math.round(Math.sqrt((n * availW) / Math.max(1, availH))) || 1, 1, n);
  const rows = Math.ceil(n / cols);
  const gap = 20;
  const bw = (availW - gap * (cols - 1)) / cols;
  const bh = Math.min((availH - gap * (rows - 1)) / rows, 260);

  const boxes = {};
  subnets.forEach((s, i) => {
    boxes[s.cidr] = {
      x: pad + (i % cols) * (bw + gap),
      y: band + Math.floor(i / cols) * (bh + gap),
      w: bw, h: bh,
    };
  });

  // A gateway belongs above the subnet it serves, which the topology already
  // tells us: its edge runs subnet -> gateway IP. Spreading gateways evenly
  // across the canvas instead sent their edges slashing diagonally over every
  // box, which read as a mesh that does not exist.
  const nodePos = {};
  const servedBy = {};
  (edges || []).forEach((e) => {
    if (boxes[e.src] && !boxes[e.dst] && servedBy[e.dst] === undefined) servedBy[e.dst] = e.src;
  });
  const unplaced = [];
  nodes.forEach((nd) => {
    const cidr = servedBy[nd.id];
    if (cidr && boxes[cidr]) {
      const b = boxes[cidr];
      nodePos[nd.id] = { x: b.x + b.w / 2, y: Math.max(26, b.y - 34) };
    } else {
      unplaced.push(nd);
    }
  });
  unplaced.forEach((nd, i) => {
    const m = unplaced.length;
    nodePos[nd.id] = { x: m === 1 ? W / 2 : pad + 60 + (i * (W - pad * 2 - 120)) / (m - 1), y: 30 };
  });
  return { boxes, nodePos, coreX: W / 2, coreY: 22 };
}

function focusLayout(subnets, focusCidr, nodes, W, H, edges) {
  const boxes = {};
  const others = subnets.filter((s) => s.cidr !== focusCidr);
  if (others.length) {
    const cw = Math.min(190, (W - 40 - 12 * (others.length - 1)) / others.length);
    const total = others.length * cw + 12 * (others.length - 1);
    let x = (W - total) / 2;
    others.forEach((s) => { boxes[s.cidr] = { x, y: 30, w: cw, h: 64 }; x += cw + 12; });
  }
  boxes[focusCidr] = { x: 70, y: others.length ? 150 : 34, w: W - 140, h: H - (others.length ? 176 : 60) };
  // Same rule as the estate: a gateway sits above the box it serves. Parking
  // them all at a fixed corner instead drew one long near-horizontal line the
  // width of the board, which reads as a stray line rather than a connection.
  const nodePos = {};
  const servedBy = {};
  (edges || []).forEach((e) => {
    if (boxes[e.src] && !boxes[e.dst] && servedBy[e.dst] === undefined) servedBy[e.dst] = e.src;
  });
  const loose = [];
  nodes.forEach((nd) => {
    const b = boxes[servedBy[nd.id]];
    if (b) nodePos[nd.id] = { x: b.x + b.w / 2, y: Math.max(16, b.y - 30) };
    else loose.push(nd);
  });
  loose.forEach((nd, i) => { nodePos[nd.id] = { x: 60 + i * 110, y: 16 }; });
  return { boxes, nodePos, coreX: W / 2, coreY: 10 };
}

// Largest square cell that fits the host count, same rule as the flat map.
function hostSlots(assets, box, big) {
  const n = assets.length;
  if (!n) return [];
  const padX = big ? 30 : 12;
  const padTop = big ? 62 : 40;
  const padBot = big ? 22 : 12;
  const iw = Math.max(20, box.w - padX * 2);
  const ih = Math.max(16, box.h - padTop - padBot);
  let cols = 1;
  let cell = 0;
  for (let c = 1; c <= n; c += 1) {
    const rows = Math.ceil(n / c);
    const s = Math.min(iw / c, ih / rows);
    if (s > cell) { cell = s; cols = c; }
  }
  const rows = Math.ceil(n / cols);
  const r = big ? clamp(cell * 0.28, 3, 17) : clamp(cell * 0.3, 1.6, 4.5);
  const gx = box.x + padX + (iw - cols * cell) / 2;
  const gy = box.y + padTop + (ih - rows * cell) / 2;
  return assets.map((a, i) => ({
    a, r, cell,
    x: gx + (i % cols) * cell + cell / 2,
    y: gy + Math.floor(i / cols) * cell + cell / 2,
  }));
}

// ---------- mesh views ----------
//
// Two ways of drawing what a flat segment implies, plus the plain board.
// A /24 is one broadcast domain, so every host pair inside it is connected and
// drawing all of them is N(N-1)/2 lines that assert one fact. `lateral` draws
// only the hosts running a service you can act THROUGH, which on a mixed
// network is a small readable graph. `full` stops drawing pairs at all and
// counts them into groups instead. Neither deletes anything: one narrows the
// subject, the other summarises it.

// Distance from a ring's radius out to its dashed hoop. Label offsets are
// measured from the hoop rather than from the radius: when the rings were
// enlarged the hoop moved out to +28 while the second label stayed at +25,
// which drew that label on top of the stroke.
const HOOP_GAP = 28;

const LATERAL_NAME = {
  22: 'SSH', 445: 'SMB', 3389: 'RDP', 5985: 'WinRM',
  5432: 'Postgres', 3306: 'MySQL', 1433: 'MSSQL', 6379: 'Redis',
};

export function meshClass(a) {
  if ((a.lateral_ports || []).length) return 'pivot';
  if (a.has_web) return 'web';
  return a.open_ports > 0 ? 'other' : 'silent';
}
const sharedLateral = (a, b) => (a.lateral_ports || []).filter((p) => (b.lateral_ports || []).includes(p));

export const MESH_GROUPS = [
  { k: 'pivot', label: 'Pivotable', sub: '22, 445, 3389, 5432 ...', color: '#7c4ad0' },
  { k: 'web', label: 'Web', sub: '80, 443, 8080, 8443', color: '#0ea5e9' },
  { k: 'other', label: 'Other svc', sub: 'everything else listening', color: '#f59e0b' },
  { k: 'silent', label: 'Silent', sub: 'nothing listening', color: '#94a3b8' },
];

// One ring per segment. The chords inside a segment stay legible at this size
// and are worth drawing per host; only what crosses a boundary gets bundled,
// which is the part that does not fit. Space beats abstraction here.
function meshRingLayout(assets, cidrs, W, H) {
  const cx = W / 2;
  const cy = H / 2 + 22;
  const rings = {};
  const segs = cidrs.filter((c) => assets.some((a) => a.subnet === c && meshClass(a) === 'pivot'));
  const use = segs.length ? segs : cidrs.slice(0, 1);
  const n = use.length;
  if (n === 1) rings[use[0]] = { x: cx, y: cy - 10 };
  else if (n === 2) { rings[use[0]] = { x: cx - W * 0.21, y: cy }; rings[use[1]] = { x: cx + W * 0.21, y: cy }; }
  else {
    // Centres on an ellipse so no connector has to cross a third ring.
    use.forEach((c, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      rings[c] = { x: cx + Math.cos(a) * W * 0.29, y: cy + Math.sin(a) * H * 0.26 };
    });
  }
  // Budget per ring from the space actually available, then spend a share of it
  // according to how many hosts the ring holds. A single segment should fill the
  // board; three should tile it without colliding.
  const budget = n === 1 ? Math.min(W, H) * 0.40
    : n === 2 ? Math.min(W * 0.26, H * 0.40)
      : Math.min(W * 0.20, H * 0.27);
  use.forEach((c) => {
    const cnt = assets.filter((a) => a.subnet === c && meshClass(a) === 'pivot').length;
    rings[c].n = cnt;
    const fill = 0.55 + 0.45 * Math.min(1, Math.sqrt(Math.max(1, cnt)) / Math.sqrt(24));
    rings[c].R = clamp(budget * fill, 46, budget);
  });

  // Each ring carries two label lines above it and a beacon line below, and on
  // a three-ring layout the topmost ring's title was running off the board.
  // Fit the whole arrangement, labels included, before anything is drawn.
  // Derived from HOOP_GAP so the two cannot drift apart again: the labels sit
  // outside the hoop, and the layout reserves exactly the room they occupy.
  const headTop = HOOP_GAP + 44;
  const headBot = HOOP_GAP + 12;
  const bounds = () => {
    let top = Infinity;
    let bot = -Infinity;
    use.forEach((c) => {
      top = Math.min(top, rings[c].y - rings[c].R - headTop);
      bot = Math.max(bot, rings[c].y + rings[c].R + headBot);
    });
    return { top, bot };
  };
  let { top, bot } = bounds();
  const have = H - 16;
  if (bot - top > have) {
    const k = have / (bot - top);
    use.forEach((c) => { rings[c].R *= k; rings[c].y = cy + (rings[c].y - cy) * k; });
    ({ top, bot } = bounds());
  }
  const shift = top < 8 ? 8 - top : (bot > H - 8 ? (H - 8) - bot : 0);
  if (shift) use.forEach((c) => { rings[c].y += shift; });

  const pos = {};
  use.forEach((c) => {
    const g = rings[c];
    const piv = assets.filter((a) => a.subnet === c && meshClass(a) === 'pivot');
    const arc = (2 * Math.PI * g.R) / Math.max(1, piv.length);
    const nodeR = clamp(arc * 0.2, 6, 17);
    piv.forEach((a, i) => {
      const ang = -Math.PI / 2 + (i * 2 * Math.PI) / piv.length;
      pos[a.id] = { x: g.x + Math.cos(ang) * g.R, y: g.y + Math.sin(ang) * g.R, r: nodeR };
    });
    // Everything else orbits its own ring, faint. It stays attached to the
    // segment it belongs to rather than being exiled to a shared outer band.
    const rest = assets.filter((a) => a.subnet === c && meshClass(a) !== 'pivot');
    rest.forEach((a, i) => {
      const ang = -Math.PI / 2 + (i * 2 * Math.PI) / Math.max(1, rest.length);
      pos[a.id] = { x: g.x + Math.cos(ang) * (g.R + nodeR + 20), y: g.y + Math.sin(ang) * (g.R + nodeR + 20), r: clamp(nodeR * 0.32, 3, 6) };
    });
  });
  assets.forEach((a) => { if (!pos[a.id]) pos[a.id] = { x: -80, y: -80, r: 0 }; });
  return { pos, rings, segs: use };
}

// Groups: by segment when there is more than one (the segment is the real
// boundary), by exposure class inside a single one (there is no boundary in
// there, so the useful split is what each host exposes).
function meshGroupsOf(assets, cidrs) {
  if (cidrs.length > 1) {
    return cidrs.map((c, i) => ({
      key: c,
      label: c,
      sub: `${assets.filter((a) => a.subnet === c).length} hosts`,
      color: ['#ef4444', '#7c4ad0', '#0ea5e9', '#f59e0b', '#00ce3e', '#64748b'][i % 6],
      members: assets.filter((a) => a.subnet === c),
    })).filter((g) => g.members.length);
  }
  return MESH_GROUPS.map((g) => ({
    key: g.k, label: g.label, sub: g.sub, color: g.color,
    members: assets.filter((a) => meshClass(a) === g.k),
  })).filter((g) => g.members.length);
}

function meshGroupLayout(assets, cidrs, W, H, expanded) {
  const gs = meshGroupsOf(assets, cidrs);
  const cx = W / 2;
  const cy = H / 2 + 10;
  const hubs = {};
  // Spread with the board rather than to fixed fractions of it: on a wide
  // monitor the old constants left a third of the canvas empty on each side.
  const spreadX = W * 0.30;
  const spreadY = H * 0.26;
  if (gs.length <= 3) {
    gs.forEach((g, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / gs.length;
      hubs[g.key] = { x: cx + Math.cos(a) * spreadX, y: cy + Math.sin(a) * spreadY };
    });
  } else {
    const q = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
    gs.forEach((g, i) => {
      hubs[g.key] = { x: cx + q[i % 4][0] * spreadX, y: cy + q[i % 4][1] * spreadY };
    });
  }
  const open = gs.find((g) => g.key === expanded);
  const pos = {};
  assets.forEach((a) => {
    const g = gs.find((x) => x.members.includes(a));
    if (!g) { pos[a.id] = { x: -80, y: -80, r: 0 }; return; }
    if (open && g.key === open.key) {
      const i = open.members.indexOf(a);
      const cnt = open.members.length;
      const uiScale = clamp(Math.min(W, H) / 700, 0.85, 1.5);
      const R = clamp(Math.sqrt(cnt) * 21 * uiScale, 52, Math.min(W, H) * 0.22);
      const ang = -Math.PI / 2 + (i * 2 * Math.PI) / cnt;
      pos[a.id] = { x: hubs[g.key].x + Math.cos(ang) * R, y: hubs[g.key].y + Math.sin(ang) * R, r: cnt > 18 ? 7.5 : 9.5 };
    } else {
      pos[a.id] = { x: hubs[g.key].x, y: hubs[g.key].y, r: 2.8 };
    }
  });
  return { pos, hubs, gs, open };
}

// A multi-homed host appears in two segments under one hardware address. That
// is a real path between them, derived from what was observed rather than
// inferred, so it is the one cross-segment line this map will draw. Host to
// host reachability across a boundary is NOT drawn: reachability is measured
// from beacons, so a line between two ordinary hosts in different segments
// would be an assertion nobody tested.
function drawBridge(sc, id) {
  const g = sc.gBridge;
  if (!g) return;
  while (g.firstChild) g.removeChild(g.firstChild);
  if (!id) return;
  const src = sc.hosts[id];
  if (!src || !src.a.multi_homed) return;
  const mac = (src.a.mac || '').toLowerCase();
  if (!mac) return;
  const twins = Object.values(sc.hosts).filter(
    (n) => n.a.id !== id && (n.a.mac || '').toLowerCase() === mac && n.a.subnet !== src.a.subnet,
  );
  twins.forEach((t) => {
    const ln = el('line', {
      x1: src.x, y1: src.y, x2: t.x, y2: t.y,
      stroke: '#7c4ad0', 'stroke-width': 2.4, 'stroke-linecap': 'round',
      'stroke-dasharray': '6 4', opacity: 0.9,
    });
    g.appendChild(ln);
    g.appendChild(el('circle', { cx: t.x, cy: t.y, r: 9, fill: 'none', stroke: '#7c4ad0', 'stroke-width': 2 }));
  });
}

// Hovering a host on the plain board fans out what it can reach. Inside a
// segment that is every other host in it, because a broadcast domain has
// nothing in between: the fan is the flat-mesh claim, drawn on demand instead
// of permanently. Lines radiate in distance order so it reads as one sweep
// rather than fifty lines arriving at once, and their colour carries what is
// listening at the far end.
// Silent hosts were #cbd5e1, which is nearly the board's own tint: half the fan
// was invisible on the very background it is drawn against.
const FAN_COLOR = { pivot: '#7c4ad0', web: '#0ea5e9', other: '#f59e0b', silent: '#94a3b8' };
function drawFan(sc, id) {
  const g = sc.gFan;
  if (!g) return;
  while (g.firstChild) g.removeChild(g.firstChild);
  const src = sc.hosts[id];
  if (!src || sc.mesh !== 'off' || !src.big) return;
  const peers = Object.values(sc.hosts)
    .filter((n) => n.a.id !== id && n.big && n.a.subnet === src.a.subnet)
    .map((n) => ({ n, d: Math.hypot(n.x - src.x, n.y - src.y) }))
    .sort((p, q) => p.d - q.d);
  if (!peers.length) return;

  const still = reduceMotion();
  const pulse = el('circle', { cx: src.x, cy: src.y, r: src.r, fill: 'none', stroke: '#7c4ad0', 'stroke-width': 2, opacity: still ? 0 : 0.85 });
  g.appendChild(pulse);
  if (!still && pulse.animate) {
    pulse.animate(
      [{ r: src.r, opacity: 0.85, strokeWidth: 2 }, { r: src.r + 230, opacity: 0, strokeWidth: 0.5 }],
      { duration: 900, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'forwards' },
    );
  }
  peers.forEach((t, i) => {
    const k = meshClass(t.n.a);
    const ln = el('line', {
      x1: src.x, y1: src.y, x2: t.n.x, y2: t.n.y,
      stroke: FAN_COLOR[k], 'stroke-width': k === 'pivot' ? 2.2 : 1.5,
      opacity: k === 'silent' ? 0.5 : 0.85, 'stroke-linecap': 'round',
    });
    g.appendChild(ln);
    if (still || !ln.animate) return;
    const len = t.d;
    ln.setAttribute('stroke-dasharray', len);
    ln.setAttribute('stroke-dashoffset', len);
    const anim = ln.animate(
      [{ strokeDashoffset: len }, { strokeDashoffset: 0 }],
      { duration: 300, delay: i * 11, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' },
    );
    anim.onfinish = () => {
      ln.removeAttribute('stroke-dasharray');
      ln.removeAttribute('stroke-dashoffset');
    };
  });
}

// One curve rule cannot serve a short hop between neighbours and a long edge
// that has to clear three boxes, so the router picks by geometry.
function route(A, B, dir, entry) {
  const ca = { x: A.x + A.w / 2, y: A.y + A.h / 2 };
  const cb = { x: B.x + B.w / 2, y: B.y + B.h / 2 };
  const sameRow = Math.abs(ca.y - cb.y) < 60;
  const fwd = cb.x > ca.x;

  if (!sameRow) {
    const down = cb.y > ca.y;
    const WIDE = 380;
    const ax = entry != null && A.w > WIDE ? A.x + A.w * entry : ca.x;
    const bx = entry != null && B.w > WIDE ? B.x + B.w * entry : cb.x;
    const a = { x: ax, y: down ? A.y + A.h : A.y };
    const b = { x: bx, y: down ? B.y : B.y + B.h };
    return { a, b, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, kind: 'v' };
  }

  const gap = Math.abs(cb.x - ca.x) - (A.w + B.w) / 2;
  if (gap < 90) {
    const lane = dir * 16;
    const a = { x: fwd ? A.x + A.w : A.x, y: ca.y - lane };
    const b = { x: fwd ? B.x : B.x + B.w, y: cb.y - lane };
    return { a, b, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, kind: 'h' };
  }

  const a = { x: ca.x, y: A.y + A.h };
  const b = { x: cb.x, y: B.y + B.h };
  return { a, b, mx: (a.x + b.x) / 2, my: Math.max(a.y, b.y) + 54 + Math.abs(cb.x - ca.x) * 0.11, kind: 'bow' };
}

const EDGE_STYLE = {
  gateway: { stroke: '#334155', width: 1.8, dash: null },
  traceroute_hop: { stroke: '#64748b', width: 1.2, dash: '3 4' },
  multi_homed: { stroke: '#7c4ad0', width: 1.8, dash: '5 3' },
};

/* Paint the scene at eased progress `e`. Split out of the animation loop so
 * build() can paint synchronously: requestAnimationFrame is throttled in
 * background tabs, under reduced motion, and in headless capture, and a map
 * that only has geometry once a frame has run renders as nothing in all three.
 */
function paintScene(sc, now, e) {
      Object.values(sc.boxes).forEach((b) => {
        b.x = lerp(b.fx, b.tx, e); b.y = lerp(b.fy, b.ty, e);
        b.w = lerp(b.fw, b.tw, e); b.h = lerp(b.fh, b.th, e);
        b.rect.setAttribute('x', b.x); b.rect.setAttribute('y', b.y);
        b.rect.setAttribute('width', Math.max(1, b.w)); b.rect.setAttribute('height', Math.max(1, b.h));
        const small = b.h < 110;
        b.rect.setAttribute('opacity', sc.bigFocus && b.cidr !== sc.bigFocus ? 0.85 : 1);
        if (sc.bigFocus === b.cidr) b.rect.setAttribute('stroke', '#a7dfa9');
        b.title.setAttribute('x', b.x + 12);
        b.title.setAttribute('y', b.y + 19);
        b.title.setAttribute('font-size', small ? 10 : 12);
        b.meta.setAttribute('x', b.x + b.w - 12);
        b.meta.setAttribute('y', b.y + 19);
        b.meta.setAttribute('font-size', small ? 8.5 : 9.5);
        const s = b.s;
        b.meta.textContent = small
          ? `${s.asset_count}`
          : `${s.asset_count} host${s.asset_count === 1 ? '' : 's'}${s.eol_count ? ` · ${s.eol_count} EOL` : ''}`;
        while (b.bar.firstChild) b.bar.removeChild(b.bar.firstChild);
        if (!small && s.asset_count > 0) {
          const order = [['critical', '#ef4444'], ['high', '#f97316'], ['medium', '#f59e0b'], ['low', '#64748b']];
          const counted = order.reduce((acc, [k]) => acc + (s.risk_counts[k] || 0), 0);
          const bw = b.w - 24;
          let bx = b.x + 12;
          order.forEach(([k, c]) => {
            const cnt = s.risk_counts[k] || 0;
            if (!cnt) return;
            const wpx = (bw * cnt) / s.asset_count;
            b.bar.appendChild(el('rect', { x: bx, y: b.y + 27, width: Math.max(1, wpx - 1), height: 4, rx: 2, fill: c }));
            bx += wpx;
          });
          const clean = s.asset_count - counted;
          if (clean > 0) {
            b.bar.appendChild(el('rect', { x: bx, y: b.y + 27, width: Math.max(1, (bw * clean) / s.asset_count), height: 4, rx: 2, fill: '#00ce3e' }));
          }
        }
      });

      // Mesh chords read the LIVE node positions, so they travel with the morph
      // rather than snapping to the destination while the hosts are in flight.
      (sc.meshLines || []).forEach((m) => {
        const a = sc.hosts[m.a];
        if (!a) return;
        m.ln.setAttribute('x1', a.x); m.ln.setAttribute('y1', a.y);
        if (m.to) { m.ln.setAttribute('x2', m.to.x); m.ln.setAttribute('y2', m.to.y); return; }
        const b = sc.hosts[m.b];
        if (!b) return;
        m.ln.setAttribute('x2', b.x); m.ln.setAttribute('y2', b.y);
        const touched = !sc.hover || sc.hover === m.a || sc.hover === m.b;
        m.ln.setAttribute('opacity', touched ? (sc.hover ? 0.95 : 0.38) : 0.05);
      });

      const ph = (now % 1800) / 1800;
      Object.values(sc.hosts).forEach((n) => {
        n.x = lerp(n.fx, n.tx, e); n.y = lerp(n.fy, n.ty, e); n.r = lerp(n.fr, n.tr, e);
        const dim = sc.hover && sc.hover !== n.a.id;
        const isHover = sc.hover === n.a.id;
        // A fan makes the other hosts the point of the picture, so they stay
        // legible; the plain hover-to-focus case still dims hard.
        const fanning = sc.mesh === 'off' && sc.hover && sc.gFan && sc.gFan.childNodes.length;
        // The estate view's dots are already small; dimming them as hard as a
        // full-size host leaves the board unreadable around the one you are on.
        const softDim = fanning || sc.level === 0;
        n.g.setAttribute('opacity', dim ? (softDim ? 0.55 : 0.24) : 1);
        const rr = isHover ? n.r * 1.3 : n.r;
        n.body.setAttribute('cx', n.x); n.body.setAttribute('cy', n.y); n.body.setAttribute('r', Math.max(0.2, rr));
        if (n.hit) {
          n.hit.setAttribute('cx', n.x); n.hit.setAttribute('cy', n.y);
          n.hit.setAttribute('r', Math.max(9, rr * 1.5));
        }
        if (n.ring) {
          n.ring.setAttribute('cx', n.x); n.ring.setAttribute('cy', n.y);
          n.ring.setAttribute('r', Math.max(0.2, rr * 1.36));
          n.ring.setAttribute('stroke-width', Math.max(1.2, n.r * 0.17));
          n.ring.setAttribute('opacity', n.r > 5 ? 1 : 0);
        }
        if (n.dring) {
          // Sits outside the EOL ring so a host can carry both.
          n.dring.setAttribute('cx', n.x); n.dring.setAttribute('cy', n.y);
          n.dring.setAttribute('r', Math.max(0.2, rr * 1.62));
          n.dring.setAttribute('stroke-width', Math.max(1, n.r * 0.14));
          n.dring.setAttribute('opacity', n.r > 5 ? 0.9 : 0);
        }
        if (n.pulse && !reduceMotion()) {
          n.pulse.setAttribute('cx', n.x); n.pulse.setAttribute('cy', n.y);
          n.pulse.setAttribute('r', Math.max(0.2, n.r + ph * 18));
          n.pulse.setAttribute('opacity', n.r > 6 ? 0.5 * (1 - ph) : 0);
        }
        const showBadge = n.r >= 10;
        n.badge.setAttribute('opacity', showBadge ? 1 : 0);
        if (showBadge) {
          n.badgeC.setAttribute('cx', n.x + rr * 0.8); n.badgeC.setAttribute('cy', n.y - rr * 0.8);
          n.badgeT.setAttribute('x', n.x + rr * 0.8); n.badgeT.setAttribute('y', n.y - rr * 0.8 + 2.8);
        }
        // Labels only when the cell can hold them without hitting the row below.
        const room = n.cell - n.r;
        const showOct = n.big && n.r >= 8.5 && room >= n.r * 1.34 + 15;
        const showHn = n.big && n.r >= 11 && room >= n.r * 1.34 + 27 && n.cell >= 62;
        n.lab.setAttribute('x', n.x); n.lab.setAttribute('y', n.y + n.r * 1.34 + 10);
        n.lab.setAttribute('opacity', showOct ? 1 : 0);
        n.hn.setAttribute('x', n.x); n.hn.setAttribute('y', n.y + n.r * 1.34 + 21);
        n.hn.setAttribute('opacity', showHn ? 1 : 0);
      });

      Object.values(sc.marks).forEach((m) => {
        m.x = lerp(m.fx, m.tx, e);
        m.y = lerp(m.fy, m.ty, e);
        m.g.setAttribute('transform', `translate(${m.x} ${m.y})`);
        sc.nodeBox[m.nd.id] = { x: m.x - 8, y: m.y - 8, w: 16, h: 16 };
      });

      const boxOf = (id) => sc.boxes[id] || sc.nodeBox[id];
      const base = (now % 2400) / 2400;
      sc.edges.forEach((o) => {
        const A = boxOf(o.e.src);
        const B = boxOf(o.e.dst);
        if (!A || !B) return;
        const far = sc.boxes[o.e.src] && sc.boxes[o.e.src].cidr === sc.bigFocus
          ? (sc.boxes[o.e.dst] ? sc.boxes[o.e.dst].idx : 0)
          : (sc.boxes[o.e.src] ? sc.boxes[o.e.src].idx : 0);
        const nSub = Object.keys(sc.boxes).length;
        const entry = sc.level === 1 ? (far + 1) / (nSub + 1) : null;
        const { a, b, mx, my } = route(A, B, o.dir, entry);
        const rel = sc.level === 0 || o.e.src === sc.bigFocus || o.e.dst === sc.bigFocus;
        const op = sc.hover ? 0.12 : (rel ? 0.7 : 0.12);
        o.path.setAttribute('d', `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`);
        o.path.setAttribute('opacity', op);
        const ang = Math.atan2(b.y - my, b.x - mx);
        const s = 6.5;
        o.head.setAttribute('d', `M ${b.x - Math.cos(ang - 0.42) * s} ${b.y - Math.sin(ang - 0.42) * s} L ${b.x} ${b.y} L ${b.x - Math.cos(ang + 0.42) * s} ${b.y - Math.sin(ang + 0.42) * s} Z`);
        o.head.setAttribute('opacity', op);
        o.dots.forEach((dt) => {
          const t = (base + dt.off) % 1;
          const mt = 1 - t;
          dt.d.setAttribute('cx', mt * mt * a.x + 2 * mt * t * mx + t * t * b.x);
          dt.d.setAttribute('cy', mt * mt * a.y + 2 * mt * t * my + t * t * b.y);
          dt.d.setAttribute('opacity', op > 0.4 ? 0.8 * Math.sin(Math.PI * t) : 0);
        });
      });
}

/* ---------------- component ---------------- */

export default function NetworkMapCanvas({
  topo, mode, pivotsOn, level, focus, mesh = 'off', expanded, onDrill, onOpenAsset,
  onExpand, onHostHover, onHostCount,
}) {
  const [wrapEl, setWrapEl] = useState(null);
  const [size, setSize] = useState({ w: 1100, h: 640 });
  const svgRef = useRef(null);
  const sceneRef = useRef(null);
  const rafRef = useRef(0);
  const snapRef = useRef(0);
  const cb = useRef({ onDrill, onOpenAsset, onExpand, onHostHover });
  cb.current = { onDrill, onOpenAsset, onExpand, onHostHover };

  // Measure synchronously before paint: the observer's first callback lands
  // after paint, which would show one frame at the placeholder size.
  useLayoutEffect(() => {
    if (!wrapEl) return undefined;
    const measure = () => {
      const r = wrapEl.getBoundingClientRect();
      setSize({ w: Math.max(360, Math.round(r.width)), h: Math.max(300, Math.round(r.height)) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapEl);
    return () => ro.disconnect();
  }, [wrapEl]);

  const build = useCallback(() => {
    const svg = svgRef.current;
    if (!svg || !topo) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const W = size.w;
    const H = size.h;
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);

    const subnets = topo.subnets || [];
    const nodes = topo.nodes || [];
    const bySubnet = {};
    (topo.assets || []).forEach((a) => { (bySubnet[a.subnet] = bySubnet[a.subnet] || []).push(a); });
    Object.values(bySubnet).forEach((list) => list.sort(
      (p, q) => (Number(String(p.ip).split('.')[3]) || 0) - (Number(String(q.ip).split('.')[3]) || 0),
    ));

    const layout = level === 1 && focus
      ? focusLayout(subnets, focus, nodes, W, H, topo.edges)
      : estateLayout(subnets, nodes, W, H, topo.edges);

    // A mesh view replaces the board rather than decorating it: subnet boxes and
    // routed edges would be a second, contradicting arrangement of the same
    // hosts on screen at once.
    const meshOn = mesh !== 'off';
    const cidrs = level === 1 && focus ? [focus] : subnets.map((sn) => sn.cidr);
    const meshAssets = (topo.assets || []).filter((a) => cidrs.includes(a.subnet));
    const meshLay = !meshOn ? null
      : mesh === 'lateral'
        ? meshRingLayout(meshAssets, cidrs, W, H)
        : meshGroupLayout(meshAssets, cidrs, W, H, expanded);

    const gEdge = el('g', {}); svg.appendChild(gEdge);
    const gFlow = el('g', {}); svg.appendChild(gFlow);
    const gBox = el('g', {}); svg.appendChild(gBox);
    // Above the boxes, below the hosts. The focused subnet box fills the board
    // at fill-opacity .95, so a fan drawn under it is seen through 95% white
    // and reads as faint no matter what colour or width it is given.
    const gFan = el('g', { 'pointer-events': 'none' }); svg.appendChild(gFan);
    const gBridge = el('g', { 'pointer-events': 'none' }); svg.appendChild(gBridge);
    const gHost = el('g', {}); svg.appendChild(gHost);
    const gLbl = el('g', {}); svg.appendChild(gLbl);
    const gNode = el('g', {}); svg.appendChild(gNode);

    // A morph animates FROM the previous layout, which is only meaningful when
    // the same board is rearranging: switching mesh, drilling into a segment.
    // A size change is not a rearrangement, it is the same arrangement measured
    // again. The canvas mounts at a placeholder width and the ResizeObserver
    // then reports the real one, so treating that as a transition made every
    // first paint slide in from the left. Re-layout instantly instead.
    const priorScene = sceneRef.current;
    const resized = !!priorScene && (priorScene.W !== W || priorScene.H !== H);
    const prev = resized ? null : priorScene;
    const boxes = {};
    const hosts = {};

    if (!meshOn) subnets.forEach((s, si) => {
      const t = layout.boxes[s.cidr];
      const old = prev && prev.boxes[s.cidr];
      const b = {
        cidr: s.cidr, s, idx: si,
        x: old ? old.x : t.x, y: old ? old.y : t.y, w: old ? old.w : t.w, h: old ? old.h : t.h,
        fx: old ? old.x : t.x, fy: old ? old.y : t.y, fw: old ? old.w : t.w, fh: old ? old.h : t.h,
        tx: t.x, ty: t.y, tw: t.w, th: t.h,
      };
      b.rect = el('rect', {
        rx: 12, fill: '#ffffff', 'fill-opacity': 0.95,
        stroke: s.eol_count > 0 ? '#f59e0b' : '#dbe4ec', 'stroke-width': 1.2,
      });
      if (level === 0) {
        b.rect.style.cursor = 'pointer';
        b.rect.addEventListener('click', () => cb.current.onDrill(s.cidr));
      }
      gBox.appendChild(b.rect);
      b.title = el('text', { 'font-size': 12, 'font-weight': 700, fill: '#0f172a', 'pointer-events': 'none', 'font-family': 'JetBrains Mono, monospace' });
      b.title.textContent = s.cidr;
      gLbl.appendChild(b.title);
      b.meta = el('text', { 'font-size': 9.5, fill: '#64748b', 'text-anchor': 'end', 'pointer-events': 'none' });
      gLbl.appendChild(b.meta);
      b.bar = el('g', { 'pointer-events': 'none' });
      gLbl.appendChild(b.bar);
      boxes[s.cidr] = b;
    });

    const bigFocus = level === 1 ? focus : null;
    const slots = meshOn
      ? (topo.assets || []).map((a) => {
        const p = (meshLay.pos && meshLay.pos[a.id]) || { x: -90, y: -90, r: 0 };
        return { a, x: p.x, y: p.y, r: p.r, cell: 44, big: p.r >= 6 };
      })
      : subnets.reduce((acc, s) => acc.concat(
        hostSlots(bySubnet[s.cidr] || [], layout.boxes[s.cidr], s.cidr === bigFocus)
          .map((sl) => Object.assign({}, sl, { big: s.cidr === bigFocus })),
      ), []);
    slots.forEach((sl) => {
        const big = sl.big;
        const a = sl.a;
        const old = prev && prev.hosts[a.id];
        const g = el('g', {});
        const n = {
          a, big, cell: sl.cell,
          x: old ? old.x : sl.x, y: old ? old.y : sl.y, r: old ? old.r : sl.r,
          fx: old ? old.x : sl.x, fy: old ? old.y : sl.y, fr: old ? old.r : sl.r,
          tx: sl.x, ty: sl.y, tr: sl.r, g,
        };
        const fill = dotColor(a, mode, pivotsOn);
        if (hot(a)) { n.pulse = el('circle', { fill: 'none', stroke: fill, 'stroke-width': 2, opacity: 0 }); g.appendChild(n.pulse); }
        if (isEol(a)) { n.ring = el('circle', { fill: 'none', stroke: '#d97706', opacity: 0 }); g.appendChild(n.ring); }
        // A host that accepts one beacon and refuses another is being filtered
        // by source address, by its own firewall or by the path from one of
        // them. Dashed so it reads as a boundary, not a property of the host.
        if (a.reach_disputed) {
          n.dring = el('circle', {
            fill: 'none', stroke: '#7c4ad0', 'stroke-dasharray': '3 3', opacity: 0,
          });
          g.appendChild(n.dring);
        }
        n.body = el('circle', {
          fill,
          stroke: a.multi_homed && pivotsOn ? '#5b21b6' : 'rgba(15,23,42,.18)',
          'stroke-width': a.multi_homed && pivotsOn ? 1.6 : 1,
        });
        g.appendChild(n.body);
        n.badge = el('g', { opacity: 0, 'pointer-events': 'none' });
        n.badgeC = el('circle', { fill: '#fff', stroke: '#94a3b8', 'stroke-width': 0.8, r: 6 });
        n.badgeT = el('text', { 'text-anchor': 'middle', 'font-size': 7.5, 'font-weight': 700, fill: '#475569' });
        n.badgeT.textContent = a.open_ports > 99 ? '99+' : a.open_ports;
        n.badge.appendChild(n.badgeC); n.badge.appendChild(n.badgeT);
        if (a.open_ports > 0) g.appendChild(n.badge);
        n.lab = el('text', { 'text-anchor': 'middle', 'font-size': 9.5, fill: '#475569', opacity: 0, 'pointer-events': 'none', 'font-family': 'JetBrains Mono, monospace' });
        n.lab.textContent = octet(a.ip);
        g.appendChild(n.lab);
        n.hn = el('text', { 'text-anchor': 'middle', 'font-size': 8.5, fill: '#94a3b8', opacity: 0, 'pointer-events': 'none' });
        n.hn.textContent = shortHost(a.hostname, 16);
        g.appendChild(n.hn);
        // Accessible name rather than an SVG <title>. A <title> renders as the
        // browser's own dark tooltip, which drew on top of the hover card and
        // said the same thing twice in worse type. aria-label keeps the node
        // named for assistive tech without producing that popup.
        g.setAttribute('role', 'img');
        g.setAttribute('aria-label', [
          a.hostname ? `${a.hostname} (${a.ip})` : a.ip,
          `risk ${a.risk}`,
          `${a.open_ports} open port${a.open_ports === 1 ? '' : 's'}`,
          a.os_name ? `${a.os_name} ${a.os_version || ''}`.trim() : null,
          a.multi_homed ? 'bridges two segments' : null,
        ].filter(Boolean).join(', '));
        // A dot in the estate view is about 4px across, which is not a hover
        // target. An invisible disc gives every host the same reachable area
        // whatever size it is drawn at.
        n.hit = el('circle', { fill: 'transparent', stroke: 'none' });
        g.appendChild(n.hit);
        g.style.cursor = 'pointer';
        g.addEventListener('click', (e) => { e.stopPropagation(); cb.current.onOpenAsset({ id: a.id }); });
        g.addEventListener('mouseenter', () => {
          const sc = sceneRef.current;
          sc.hover = a.id;
          drawFan(sc, a.id);
          drawBridge(sc, a.id);
          if (cb.current.onHostHover) {
            cb.current.onHostHover({ asset: a, x: sc.hosts[a.id].x, y: sc.hosts[a.id].y });
          }
        });
        g.addEventListener('mouseleave', () => {
          const sc = sceneRef.current;
          if (sc.hover !== a.id) return;
          sc.hover = null;
          drawFan(sc, null);
          drawBridge(sc, null);
          if (cb.current.onHostHover) cb.current.onHostHover(null);
        });
        gHost.appendChild(g);
        hosts[a.id] = n;
    });

    // ---- mesh ink ----
    // Chords key on node ids and are repositioned every frame from the live
    // host positions, so they travel with the morph instead of snapping.
    const meshLines = [];
    if (meshOn && mesh === 'lateral') {
      const { rings, segs } = meshLay;
      segs.forEach((cidr) => {
        const g = rings[cidr];
        const piv = meshAssets.filter((a) => a.subnet === cidr && meshClass(a) === 'pivot');
        for (let i = 0; i < piv.length; i += 1) {
          for (let j = i + 1; j < piv.length; j += 1) {
            const shared = sharedLateral(piv[i], piv[j]);
            if (!shared.length) continue;
            const ln = el('line', {
              stroke: '#7c4ad0', 'stroke-width': Math.min(2.4, 0.9 + shared.length * 0.5),
              opacity: 0.38, 'stroke-linecap': 'round',
            });
            gEdge.appendChild(ln);
            meshLines.push({ ln, a: piv[i].id, b: piv[j].id });
          }
        }
        const hoop = el('circle', {
          cx: g.x, cy: g.y, r: g.R + HOOP_GAP, fill: 'none', stroke: '#7c4ad0',
          'stroke-width': 1.4, opacity: 0.4, 'stroke-dasharray': '4 5',
        });
        gEdge.appendChild(hoop);
        const inner = (g.n * (g.n - 1)) / 2;
        const t1 = el('text', { x: g.x, y: g.y - g.R - HOOP_GAP - 33, 'text-anchor': 'middle', 'font-size': 13, 'font-weight': 800, fill: '#7c4ad0', 'pointer-events': 'none', 'font-family': MONO_FONT });
        t1.textContent = `${cidr} · ${g.n} pivotable`;
        const t2 = el('text', { x: g.x, y: g.y - g.R - HOOP_GAP - 18, 'text-anchor': 'middle', 'font-size': 10.5, fill: '#64748b', 'pointer-events': 'none', 'font-family': UI_FONT });
        t2.textContent = `${inner} path${inner === 1 ? '' : 's'} inside, nothing in the way`;
        gLbl.appendChild(t1); gLbl.appendChild(t2);
      });

      // Only what crosses a segment boundary is bundled, into one connector per
      // pair. Measured reachability decides whether it is drawn as live.
      if (segs.length > 1) {
        const openTo = (dst, port) => (dst.reach_measured
          ? (dst.reach_open_ports || []).includes(port) : null);
        for (let x = 0; x < segs.length; x += 1) {
          for (let y = x + 1; y < segs.length; y += 1) {
            const A = segs[x]; const B = segs[y];
            const ga = rings[A]; const gb = rings[B];
            const pa = meshAssets.filter((a) => a.subnet === A && meshClass(a) === 'pivot');
            const pb = meshAssets.filter((a) => a.subnet === B && meshClass(a) === 'pivot');
            const shared = [];
            let anyMeasured = false;
            let anyOpen = 0;
            Object.keys(LATERAL_NAME).map(Number).forEach((port) => {
              const fa = pa.filter((a) => (a.lateral_ports || []).includes(port));
              const fb = pb.filter((a) => (a.lateral_ports || []).includes(port));
              if (!fa.length || !fb.length) return;
              let open = 0;
              fb.concat(fa).forEach((d) => { const o = openTo(d, port); if (o !== null) { anyMeasured = true; if (o) open += 1; } });
              anyOpen += open;
              shared.push(`${LATERAL_NAME[port]} ${fa.length}\u00d7${fb.length}`);
            });
            if (!shared.length) continue;
            const ux = gb.x - ga.x; const uy = gb.y - ga.y;
            const ul = Math.hypot(ux, uy) || 1;
            const p1 = { x: ga.x + (ux / ul) * (ga.R + 17), y: ga.y + (uy / ul) * (ga.R + 17) };
            const p2 = { x: gb.x - (ux / ul) * (gb.R + 17), y: gb.y - (uy / ul) * (gb.R + 17) };
            const live = anyOpen > 0;
            gEdge.appendChild(el('line', {
              x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y,
              stroke: live ? '#0ea5e9' : '#94a3b8', 'stroke-width': live ? 3 : 2,
              'stroke-dasharray': live ? '' : '2 7', opacity: 0.8, 'stroke-linecap': 'round',
            }));
            const mx = (p1.x + p2.x) / 2; const my = (p1.y + p2.y) / 2;
            const l2 = !anyMeasured ? 'shares a service, not probed'
              : live ? `${anyOpen} host${anyOpen === 1 ? '' : 's'} answered across` : 'blocked on every probed pair';
            const bw = Math.max(shared.join(', ').length * 5.2, l2.length * 5.2) + 18;
            gLbl.appendChild(el('rect', { x: mx - bw / 2, y: my - 17, width: bw, height: 34, rx: 9, fill: '#fff', stroke: live ? '#bae6fd' : '#e2e8f0' }));
            const c1 = el('text', { x: mx, y: my - 3, 'text-anchor': 'middle', 'font-size': 10, 'font-weight': 700, fill: live ? '#0369a1' : '#64748b' });
            c1.textContent = shared.join(', ');
            const c2 = el('text', { x: mx, y: my + 10, 'text-anchor': 'middle', 'font-size': 9.5, fill: '#94a3b8' });
            c2.textContent = l2;
            gLbl.appendChild(c1); gLbl.appendChild(c2);
          }
        }
      }
    }

    if (meshOn && mesh === 'full') {
      const { hubs, gs, open } = meshLay;
      // An opened group's per-host fan replaces its trunks: drawing both states
      // the same pairs twice, once as a number and once as lines.
      for (let x = 0; x < gs.length; x += 1) {
        for (let y = x + 1; y < gs.length; y += 1) {
          const A = gs[x]; const B = gs[y];
          if (open && (open.key === A.key || open.key === B.key)) continue;
          const pairs = A.members.length * B.members.length;
          const ha = hubs[A.key]; const hb = hubs[B.key];
          const w = clamp(Math.sqrt(pairs) / 2.4, 2, 16);
          gEdge.appendChild(el('line', { x1: ha.x, y1: ha.y, x2: hb.x, y2: hb.y, stroke: '#cbd5e1', 'stroke-width': w, opacity: 0.75, 'stroke-linecap': 'round' }));
          const t = 0.3 + ((x + y) % 3) * 0.14;
          const mx = ha.x + (hb.x - ha.x) * t; const my = ha.y + (hb.y - ha.y) * t;
          const lab = `${pairs.toLocaleString()} paths`;
          const bw = lab.length * 6.2 + 18;
          gLbl.appendChild(el('rect', { x: mx - bw / 2, y: my - 10, width: bw, height: 20, rx: 9, fill: '#fff', stroke: '#cbd5e1' }));
          const tx = el('text', { x: mx, y: my + 4, 'text-anchor': 'middle', 'font-size': 10, 'font-weight': 700, fill: '#334155' });
          tx.textContent = lab;
          gLbl.appendChild(tx);
        }
      }
      if (open) {
        gs.filter((g) => g.key !== open.key).forEach((g) => {
          const t = hubs[g.key];
          open.members.forEach((m) => {
            const ln = el('line', { x2: t.x, y2: t.y, stroke: g.color, 'stroke-width': 1, opacity: 0.28, 'stroke-linecap': 'round' });
            gEdge.appendChild(ln);
            meshLines.push({ ln, a: m.id, to: { x: t.x, y: t.y } });
          });
        });
      }
      gs.forEach((g) => {
        const h = hubs[g.key];
        const isOpen = !!open && open.key === g.key;
        const uiScale = clamp(Math.min(W, H) / 700, 0.85, 1.5);
        const R = isOpen
          ? clamp(Math.sqrt(g.members.length) * 21 * uiScale, 52, Math.min(W, H) * 0.22) + 24
          : clamp(Math.sqrt(g.members.length) * 14 * uiScale, 34, 110);
        const ring = el('circle', {
          cx: h.x, cy: h.y, r: R, fill: isOpen ? 'transparent' : `${g.color}14`,
          stroke: g.color, 'stroke-width': 1.8, 'stroke-dasharray': isOpen ? '5 4' : '',
        });
        ring.style.cursor = 'pointer';
        ring.addEventListener('click', (ev) => {
          ev.stopPropagation();
          if (cb.current.onExpand) cb.current.onExpand(isOpen ? null : g.key);
        });
        gBox.appendChild(ring);
        const big = R > 60;
        const nTx = el('text', {
          x: h.x, y: isOpen ? h.y - R - 17 : h.y + (big ? -2 : 0),
          'text-anchor': 'middle', 'font-size': isOpen ? 16 : clamp(R * 0.46, 20, 40),
          'font-weight': 800, fill: g.color, 'pointer-events': 'none', 'font-family': UI_FONT,
        });
        nTx.textContent = g.members.length;
        const lTx = el('text', {
          x: h.x, y: isOpen ? h.y - R - 2 : h.y + clamp(R * 0.34, 16, 30),
          'text-anchor': 'middle', 'font-size': clamp(R * 0.17, 11, 15),
          'font-weight': 700, fill: '#0f172a', 'pointer-events': 'none',
          'letter-spacing': '0.01em',
          'font-family': cidrs.length > 1 ? MONO_FONT : UI_FONT,
        });
        lTx.textContent = g.label;
        const inner = (g.members.length * (g.members.length - 1)) / 2;
        const iTx = el('text', {
          x: h.x, y: isOpen ? h.y + R + 16 : h.y + clamp(R * 0.34, 16, 30) + 15,
          'text-anchor': 'middle', 'font-size': 10, fill: '#64748b',
          'pointer-events': 'none', 'font-family': UI_FONT,
        });
        iTx.textContent = inner ? `${inner.toLocaleString()} paths inside${isOpen ? ', click to close' : ''}` : '';
        const sTx = el('text', {
          x: h.x, y: isOpen ? h.y + R + 30 : h.y + R + 17,
          'text-anchor': 'middle', 'font-size': 9.5, fill: '#94a3b8',
          'pointer-events': 'none', 'font-family': MONO_FONT,
        });
        sTx.textContent = g.sub;
        [nTx, lTx, iTx, sTx].forEach((e) => gLbl.appendChild(e));
      });
    }

    // Gateway and router nodes keep their own small marks. A mesh view has no
    // routed topology on screen, so their marks would float unattached.
    const nodeMarks = (meshOn ? [] : nodes).map((nd) => {
      const p = layout.nodePos[nd.id] || { x: W / 2, y: 20 };
      const oldN = prev && prev.marks && prev.marks[nd.id];
      const g = el('g', { transform: `translate(${oldN ? oldN.x : p.x} ${oldN ? oldN.y : p.y})` });
      const gw = nd.kind === 'gateway';
      g.appendChild(el('circle', { r: gw ? 7 : 5.5, fill: gw ? '#334155' : 'none', stroke: '#334155', 'stroke-width': gw ? 1 : 1.4 }));
      const t = el('text', { y: 16, 'text-anchor': 'middle', 'font-size': 7.5, fill: '#64748b', 'font-family': 'JetBrains Mono, monospace' });
      t.textContent = nd.id;
      g.appendChild(t);
      gNode.appendChild(g);
      return {
        nd, g,
        x: oldN ? oldN.x : p.x, y: oldN ? oldN.y : p.y,
        fx: oldN ? oldN.x : p.x, fy: oldN ? oldN.y : p.y,
        tx: p.x, ty: p.y,
      };
    });
    // Nodes are keyed so the next build animates them from where they are, and
    // edge endpoints read the LIVE position rather than the target: otherwise a
    // gateway snaps to the new layout while its subnet box is still travelling,
    // and the edge between them stretches across the whole board.
    const marks = {};
    nodeMarks.forEach((m) => { marks[m.nd.id] = m; });
    const nodeBox = {};

    // Edges: whatever the topology actually reports. Endpoints may be a subnet
    // CIDR or a gateway/router node, so both resolve through one lookup.
    const idxOf = (id) => (boxes[id] ? boxes[id].idx : -1);
    const edges = (topo.edges || []).filter((e) => (boxes[e.src] || nodeBox[e.src]) && (boxes[e.dst] || nodeBox[e.dst]))
      .map((e) => {
        const st = EDGE_STYLE[e.kind] || EDGE_STYLE.gateway;
        const o = { e, st, dir: idxOf(e.dst) > idxOf(e.src) ? 1 : -1 };
        o.path = el('path', { fill: 'none', stroke: st.stroke, 'stroke-width': st.width, 'stroke-linecap': 'round', opacity: 0.6 });
        if (st.dash) o.path.setAttribute('stroke-dasharray', st.dash);
        gEdge.appendChild(o.path);
        o.head = el('path', { fill: st.stroke, opacity: 0.6 });
        gEdge.appendChild(o.head);
        o.dots = [0, 1].map((i) => {
          const d = el('circle', { r: 2.2, fill: st.stroke, opacity: 0 });
          gFlow.appendChild(d);
          return { d, off: i / 2 };
        });
        return o;
      });

    sceneRef.current = {
      boxes, hosts, edges, nodeBox, marks, bigFocus, W, H, t0: performance.now(),
      hover: prev ? prev.hover : null, mode, pivotsOn, level, meshLines, mesh, gFan, gBridge,
    };
    // First paint happens here, not on the next frame.
    paintScene(sceneRef.current, performance.now(), reduceMotion() ? 1 : 0);
    // If the browser never delivers a frame (throttled background tab, a
    // low-power mode, headless capture), do not sit forever half way through a
    // transition: snap to the destination shortly after.
    clearTimeout(snapRef.current);
    snapRef.current = setTimeout(() => {
      const cur = sceneRef.current;
      // `done` means the transition actually reached its end, not merely that
      // some frame ran: one frame followed by silence is the exact case this
      // guards against.
      if (cur && !cur.done) paintScene(cur, performance.now(), 1);
    }, morphMs() + 140);
    if (onHostCount) onHostCount(bigFocus ? (bySubnet[bigFocus] || []).length : (topo.assets || []).length);
  }, [topo, size.w, size.h, level, focus, mode, pivotsOn, mesh, expanded, onHostCount]);

  useEffect(() => { build(); }, [build]);

  useEffect(() => {
    const DUR = morphMs();
    const frame = (now) => {
      const sc = sceneRef.current;
      if (sc) {
        const p = clamp((now - sc.t0) / DUR, 0, 1);
        if (p >= 1) sc.done = true;
        paintScene(sc, now, ease(p));
      }
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(rafRef.current); clearTimeout(snapRef.current); };
  }, []);

  return (
    <div
      ref={setWrapEl}
      className="relative overflow-hidden bg-gradient-to-b from-[#eef5f0] to-[#e9eff5] select-none"
      style={{ height: 'clamp(440px, 70vh, 840px)' }}
    >
      <svg ref={svgRef} width={size.w} height={size.h} className="block" role="img" aria-label="Network topology map" />
    </div>
  );
}
